export type FormatAmountOptionsType = {
  decimals?: number;
  decimalsSeparator?: string;
  digits?: number;
  digitsSeparator?: string;
  maxValue?: number;
  isAvailableFloat?: boolean;
  isAvailableNegative?: boolean;
  isSplit?: boolean;
  isSliced?: boolean;
}

export const DEFAULT_DECIMALS = 2
export const DEFAULT_DECIMALS_SEPARATOR = '.'
export const DEFAULT_DIGITS = 3
export const DEFAULT_DIGITS_SEPARATOR = ' '
export const DEFAULT_MAX_VALUE = NaN
export const DEFAULT_IS_AVAILABLE_FLOAT = true
export const DEFAULT_IS_AVAILABLE_NEGATIVE = false
export const DEFAULT_IS_SPLIT = true
export const DEFAULT_IS_SLICED = true
export const USDT_DECIMALS = 6
export const DEFAULT_AMOUNT_FORMATTER_OPTIONS: FormatAmountOptionsType = {
  decimals: DEFAULT_DECIMALS,
  decimalsSeparator: DEFAULT_DECIMALS_SEPARATOR,
  digits: DEFAULT_DIGITS,
  digitsSeparator: DEFAULT_DIGITS_SEPARATOR,
  maxValue: DEFAULT_MAX_VALUE,
  isAvailableFloat: DEFAULT_IS_AVAILABLE_FLOAT,
  isAvailableNegative: DEFAULT_IS_AVAILABLE_NEGATIVE,
  isSplit: DEFAULT_IS_SPLIT,
  isSliced: DEFAULT_IS_SLICED
}
export const DEFAULT_PERCENT_FORMATTER_OPTIONS: FormatAmountOptionsType = {
  ...DEFAULT_AMOUNT_FORMATTER_OPTIONS,
  maxValue: 100,
  isAvailableFloat: false,
  isSplit: false
}
export const USDT_AMOUNT_FORMATTER_OPTIONS: FormatAmountOptionsType = {
  ...DEFAULT_AMOUNT_FORMATTER_OPTIONS,
  decimals: USDT_DECIMALS
}

export const convertFromUnits = (value: number | string, decimals: number = DEFAULT_DECIMALS) => {
  const valueToNumber = Number(value)

  if (!valueToNumber) {
    return valueToNumber
  }

  return valueToNumber / (10 ** decimals)
}
export const convertToUnits = (value: number | string, decimals: number = DEFAULT_DECIMALS) => {
  const valueToNumber = Number(value)

  if (!valueToNumber) {
    return valueToNumber
  }

  return Math.round(valueToNumber * (10 ** decimals))
}

export const replaceDotWithDecimalsSeparator = (value: number | string, decimalsSeparator: string = DEFAULT_DECIMALS_SEPARATOR) =>
  `${value}`.replace('.', decimalsSeparator)

export const conformToAmountSymbols = (
  value: string | number,
  options: FormatAmountOptionsType = DEFAULT_AMOUNT_FORMATTER_OPTIONS,
  isRawValue: boolean = true
) => {
  const allOptions: FormatAmountOptionsType = {
    ...DEFAULT_AMOUNT_FORMATTER_OPTIONS,
    ...options
  }
  const separator = allOptions.decimalsSeparator!

  let preparedValue = isRawValue ? replaceDotWithDecimalsSeparator(value, separator) : `${value}`
  const isNegativeValue = Array.from(preparedValue)[0] === '-'

  if (isNegativeValue) {
    preparedValue = preparedValue.slice(1)
  }

  if (!preparedValue || preparedValue === '-') {
    return isNegativeValue && allOptions.isAvailableNegative ? '-' : preparedValue
  }

  if (!allOptions.isAvailableFloat) {
    const onlyNumbersRule = /[^\d]/g
    const noStartZerosRule = /^[0]+/

    return preparedValue.replace(onlyNumbersRule, '').replace(noStartZerosRule, '')
  }

  const firstRule = new RegExp(`[^\\d\\${separator}]`, 'g')
  const secondRule = new RegExp(`^\\${separator}`)
  const thirdRule = new RegExp(`\\${separator}`)
  const fourthRule = new RegExp(`\\${separator}`, 'g')
  const uniqSymbol = Symbol('').toString()

  let clearedValue = preparedValue
    // Удаляем всё кроме цифр и разделителя дробных чисел
    .replace(firstRule, '')
    // Удаляем разделитель, если он вначале строки
    .replace(secondRule, '')

  // Если вначале строки ноль, а следом за ним любой символ, кроме разделителя, то добавляем разделитель после этого ноля
  if (clearedValue.length > 1 && Array.from(clearedValue)[0] === '0' && Array.from(clearedValue)[1] !== separator) {
    clearedValue = `0${separator}${clearedValue.slice(1)}`
  }

  const resultValue = clearedValue
    // Следующие три действия осуществляют запрет ввода более одного разделителя
    // Заменяем первый найденный разделитель на уникальный символ
    .replace(thirdRule, uniqSymbol)
    // Удаляем все оставшиеся разделители
    .replace(fourthRule, '')
    // Заменяем обратно уникальный символ на разделитель
    .replace(uniqSymbol, separator)

  return isNegativeValue && allOptions.isAvailableNegative ? `-${resultValue}` : resultValue
}

export const splitIntegerByDigits = (value: string | number, options: FormatAmountOptionsType = DEFAULT_AMOUNT_FORMATTER_OPTIONS) => {
  const allOptions: FormatAmountOptionsType = {
    ...DEFAULT_AMOUNT_FORMATTER_OPTIONS,
    ...options
  }

  let preparedValue = `${value}`

  const isNegativeValue = Array.from(preparedValue)[0] === '-'

  if (isNegativeValue) {
    preparedValue = preparedValue.slice(1)
  }

  if (!preparedValue || preparedValue === '-') {
    return isNegativeValue && allOptions.isAvailableNegative ? '-' : preparedValue
  }

  const regExpRule = new RegExp(`.{1,${allOptions.digits}}`, 'g')

  // Переворачиваем строку '1234567' -> '7654321'
  const reversedValue = preparedValue.split('').reverse().join('')
  // Возвращаем список совпадений по условию регулярного выражения (выбор групп символов от 1 до n) '7654321' -> ['765', '432', '1']
  const splittedValue = reversedValue.match(regExpRule)
  // Переворачиваем каждый элемент в полученном списке ['765', '432', '1'] -> ['567', '234', '1']
  const mappedValue = splittedValue!.map(digitGroup => digitGroup.split('').reverse().join(''))
  // Переворачиваем массив и соединяем его в строку через разделитель разрядов ['567', '234', '1'] -> ['1', '234', '567'] -> '1 234 567'
  const resultValue = mappedValue.reverse().join(allOptions.digitsSeparator!).trim()

  return isNegativeValue && allOptions.isAvailableNegative ? `-${resultValue}` : resultValue
}

export const fillEmptyDecimals = (value: string | number, decimals: number = DEFAULT_DECIMALS, decimalsSeparator: string = DEFAULT_DECIMALS_SEPARATOR) => {
  const preparedValue = `${value}`
  if (preparedValue.indexOf(decimalsSeparator) === -1) {
    const zeros = '0'.repeat(decimals)
    return `${value}${decimalsSeparator}${zeros}`
  }

  const splittedValue = preparedValue.split(decimalsSeparator)

  if (splittedValue.length === 1) {
    const zeros = '0'.repeat(decimals)
    return `${value}${zeros}`
  }

  const fractionPart = splittedValue[1]
  const difference = decimals - fractionPart.length

  if (difference <= 0) {
    return value
  }

  const zeros = '0'.repeat(difference)
  return `${value}${zeros}`
}

export const formatAmount = (value: string | number, options: FormatAmountOptionsType = DEFAULT_AMOUNT_FORMATTER_OPTIONS, isRawValue: boolean = true) => {
  const allOptions: FormatAmountOptionsType = {
    ...DEFAULT_AMOUNT_FORMATTER_OPTIONS,
    ...options
  }

  let preparedValue = `${value}`

  const isNegativeValue = Array.from(preparedValue)[0] === '-'

  if (isNegativeValue) {
    preparedValue = preparedValue.slice(1)
  }

  if (!preparedValue || preparedValue === '-') {
    return isNegativeValue && allOptions.isAvailableNegative ? '-' : preparedValue
  }

  let conformedValue = conformToAmountSymbols(preparedValue, allOptions, isRawValue)
  const recoveredValue = isNegativeValue ? `-${conformedValue}` : conformedValue

  if (!isNaN(allOptions.maxValue!) && !isNaN(Number(recoveredValue)) && Number(recoveredValue) > allOptions.maxValue!) {
    conformedValue = allOptions.maxValue! >= 0 ? `${allOptions.maxValue}` : `${allOptions.maxValue}`.slice(1)
  }

  if (conformedValue.indexOf(allOptions.decimalsSeparator!) !== -1) {
    const splittedValue = conformedValue.split(allOptions.decimalsSeparator!)
    const integerPart = allOptions.isSplit
      ? splitIntegerByDigits(splittedValue[0], allOptions)
      : splittedValue[0]
    const fractionPart = allOptions.isSliced
      ? splittedValue[1]?.slice(0, allOptions.decimals!) ?? ''
      : splittedValue[1] || ''
    const concatinatedValue = `${integerPart}${allOptions.decimalsSeparator}${fractionPart}`

    return isNegativeValue && allOptions.isAvailableNegative ? `-${concatinatedValue}` : concatinatedValue
  }

  const resultValue = allOptions.isSplit
    ? splitIntegerByDigits(conformedValue, allOptions)
    : conformedValue

  return isNegativeValue && allOptions.isAvailableNegative ? `-${resultValue}` : resultValue
}

export const recoverAmount = (value: string | number, toNumberFormat: boolean = false, options: FormatAmountOptionsType = DEFAULT_AMOUNT_FORMATTER_OPTIONS) => {
  const allOptions: FormatAmountOptionsType = {
    ...DEFAULT_AMOUNT_FORMATTER_OPTIONS,
    ...options
  }

  const digitsSeparatorRule = new RegExp(`\\${allOptions.digitsSeparator!}`, 'g')
  const preparedValue = `${value}`.replace(digitsSeparatorRule, '')

  return toNumberFormat ? preparedValue.replace(allOptions.decimalsSeparator!, '.') : preparedValue
}
